import { classes } from 'classifizer';
import { useSelector } from 'react-redux';

import { TAppState } from '../../../redux';

import styles from './NoteStats.module.css';
import { ReactNode, useContext } from 'react';
import { EditabilityContext } from 'apps/text_client/src/context/EditabilityContext';
import { compileCollection } from '@ws/shared/utils';
import { TCollection } from '@ws/shared/types';
import { getLengthFromText } from '../../../utils/stats/getTextLengthFromHtml';

export function NoteStatsContainer({ children }: { children: ReactNode }) {
  return <div className={styles['note-stats-container']}>{children}</div>;
}

export function MainNoteStats({ className }: INoteStatsProps) {
  const selected = useSelector((state: TAppState) => state.editor.selected);

  if (!selected || selected.type !== 'note') {
    return null;
  }

  return <NoteStats className={className} entityId={selected.id} />;
}

export function SecondaryNoteStats({ className }: INoteStatsProps) {
  const selected = useSelector((state: TAppState) => state.editor.secondarySelected);

  if (!selected || selected.type !== 'note') {
    return null;
  }

  return <NoteStats className={className} entityId={selected.id} />;
}

export interface INoteStatsProps {
  className?: string;
  entityId: string;
}

export function NoteStats({ className, entityId }: INoteStatsProps) {
  const { getNodeByRef, getNodesByRefs } = useContext(EditabilityContext);
  const project = useSelector((state: TAppState) => state.editor.project);

  const counters = useSelector((state: TAppState) => state.stats.counters);
  const counter = counters[entityId];

  if (!project || !counter) {
    return null;
  }

  const rootCollection = getNodeByRef({ id: project.root, type: 'collection' });
  if (!rootCollection) {
    return null;
  }
  const compiledList = compileCollection({
    collection: rootCollection as TCollection,
    getNodesByRefs,
  });

  const total = { words: 0, chars: 0 };
  compiledList.forEach((formatted) => {
    if (formatted.withTitle) {
      const titleCounter = getLengthFromText(formatted.name);
      total.words += titleCounter.words;
      total.chars += titleCounter.chars;
    }

    const noteCounter = counters[formatted.id];
    if (!noteCounter) {
      return;
    }

    total.words += noteCounter.words;
    total.chars += noteCounter.chars;
  });

  return (
    <div
      className={classes(
        styles['note-stats-position'],
        styles['note-stats-visibility'],
        styles['note-stats'],
        className,
      )}
    >
      <div>
        <Head>В заметке:</Head>
        <Line title="Символов:" value={`${counter.chars}`} />
        <Line title="Слов:" value={`${counter.words}`} />
      </div>
      <hr />
      <div>
        <Head>В проекте:</Head>
        <Line title="Символов:" value={`${total.chars}`} />
        <Line title="Слов:" value={`${total.words}`} />
      </div>
    </div>
  );
}

function Head({ children }: { children: string }) {
  return <div className={styles['note-stats__head']}>{children}</div>;
}

function Line({ title, value }: { title: string; value: string }) {
  return (
    <div className={styles['note-stats__line']}>
      <span className={styles['note-stats__key']}>{title}</span>
      <span className={styles['note-stats__value']}>{value}</span>
    </div>
  );
}
